import React, { FC, useContext, useMemo, useState } from 'react';

import BasicButton from '../../components/buttons/basicButton';
import RadioInput from '../../components/inputs/radioInput';
// components
import BasicModal from '../../components/modals/basicModal';
import styled from 'styled-components';

// theme
import aampeTheme from '../../constants/aampeTheme';

// context
import { EditorContext } from '../../context/EditorContext';

// models
import {
    ApprovedFormulaState,
    FormulaDetailModel,
    FormulaEditOptions,
    FormulaStatus,
} from '../../models/formula.model';

interface FormulaEditModalProps {
    onClose: () => void;
    onEditLiveMessage: () => void;
    onEditMetadata: () => void;
    formulaFromProps?: FormulaDetailModel | null;
    className?: string;
}

const FormulaEditModal: FC<React.PropsWithChildren<FormulaEditModalProps>> = ({
    onClose,
    onEditLiveMessage,
    onEditMetadata,
    formulaFromProps,
    className,
}) => {
    const {
        editorDetails: { formula: formulaFromContext },
    } = useContext(EditorContext);

    const [selectedOption, setSelectedOption] = useState<string>('');

    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) =>
        setSelectedOption(event.target.value);

    const handleConfirm = () => {
        if (selectedOption === FormulaEditOptions.metadata) {
            onEditMetadata();
        } else {
            onEditLiveMessage();
        }
        onClose();
    };

    const formula = formulaFromProps ? formulaFromProps : formulaFromContext;
    const title = useMemo<string | undefined>(() => {
        switch (formula?.active_state) {
            case ApprovedFormulaState.Live:
                return 'Edit live message';
            case ApprovedFormulaState.Scheduled:
                return 'Edit scheduled message';
            case FormulaStatus.Review:
                return 'Edit message in review';
        }
    }, [formula]);

    return (
        <BasicModal
            header={title}
            width="512px"
            headerStyle={{
                display: 'flex',
                flexWrap: 'wrap',
                alignContent: 'center',
                justifyContent: 'space-between',
                fontSize: '24px',
                fontWeight: 500,
                color: aampeTheme.colors.neutral09,
                backgroundColor: aampeTheme.colors.white,
                padding: aampeTheme.spacing4(6),
            }}
            childContainerStyle={{
                padding: aampeTheme.spacing4(0, 6, 6, 6),
            }}
            className={className}
        >
            <ModalDescriptionText>
                {' '}
                {`You are about to ${title?.toLowerCase()}. Updates will be applied immediately. To make changes without immediate application, move the message to Drafts.`}
            </ModalDescriptionText>
            <StyledRadioInput
                descriptionText="Edit setup"
                value={FormulaEditOptions.metadata}
                selectedOption={selectedOption}
                onChange={handleRadioChange}
                helperText="Edit metadata like message name, tags, triggers."
                className={className}
            />
            <StyledRadioInput
                descriptionText="Edit message"
                value={FormulaEditOptions.message}
                selectedOption={selectedOption}
                onChange={handleRadioChange}
                helperText="Edit the alternates and labels of the message."
                className={className}
            />
            <EditButtonWrapper className={className}>
                <StyledButton
                    name="Cancel"
                    action={onClose}
                    isPrimary={false}
                />
                <StyledButton
                    name={title}
                    isPrimary={true}
                    action={handleConfirm}
                    disabled={!selectedOption}
                />
            </EditButtonWrapper>
        </BasicModal>
    );
};

export default FormulaEditModal;
const EditButtonWrapper = styled.div({
    display: 'flex',
    gap: aampeTheme.spacing(2),
    margin: aampeTheme.spacing(4, 0, 2, 0),
});
const ModalDescriptionText = styled.div({
    fontWeight: 400,
    fontSize: '16px',
    color: aampeTheme.colors.neutral09,
    lineHeight: '24px',
    padding: aampeTheme.spacing(0, 0, 1, 0),
});

const StyledButton = styled(BasicButton)({
    width: '50%',
    fontSize: '16px',
    fontWeight: 500,
});

const StyledRadioInput = styled(RadioInput)({
    '& > div': {
        display: 'flex',
        flexDirection: 'column',
        gap: aampeTheme.spacing(1),
        '& > :first-child': {
            fontSize: '16px',
            fontWeight: 500,
            color: aampeTheme.colors.gray950,
            lineHeight: '20px',
        },
        '& > :last-child': {
            fontSize: '16px',
            fontWeight: 400,
            color: aampeTheme.colors.neutral09,
            lineHeight: '20px',
        },
    },
});
